import React, { useCallback, useEffect, useState, useContext } from 'react';
import PropTypes from 'prop-types';
import { Formik, yupToFormErrors } from 'formik';
import dateFnsFormat from 'date-fns/format';
import isFuture from 'date-fns/isFuture';
import isBefore from 'date-fns/isBefore';
import startOfDay from 'date-fns/startOfDay';
import { fromJS } from 'immutable';
import { observer } from 'mobx-react';
import * as Sentry from '@sentry/react';
import { isFarmBasedPolicyType } from '@ourbranch/farm-utils';
import { policyTypes } from '@ourbranch/policy-types';
import { coverageRSExtendedForcedStates } from '@ourbranch/roof-helpers';
import { policyDetailsFromSegments } from '@ourbranch/policy-utils';

import { getPolicyStatus, PolicyStatus } from 'core/helpers/policy-status';
import { appConfig } from 'brand-config';
import { types } from 'core/helpers/sanitize';
import { AuthContext } from 'core/components/auth';
import { mapDiscountToInputs, awsDateFormatter, getAutoMPD } from 'core/helpers/formatters';
import { Loading } from 'core/components/loading';
import { useToast } from 'core/components/toast';
import { formatSPP, getValues, sortItems } from 'core/helpers/scheduled-pp-helper';
import { useStore } from 'core/store';
import { useDisabledState } from 'common/disabled-context/context';
import { useScroll } from 'core/hooks/use-scroll';
import { formatCondoValues } from 'common/components/condo/detail/helper';
import { hydrateFarmPartyForForm } from 'common/helpers/farm-interested-party-helpers';
import { applyFarmMortgagesToLocations, flattenFarmMortgagesForForm } from 'common/helpers/farm-mortgage-helpers';
import { formatUmbrellaValues } from 'common/components/umbrella/umbrella';
import { isUnderwriter } from 'common/components/farm/locations/risks/risk-types/FO6.utils';
import {
  getFarmPolicyRestriction,
  shouldRelaxFarmUWRequirements,
  wasRenewedFromImportedLineage
} from 'common/components/farm/uw-questions-form/helpers';
import {
  michiganPipInitialValuesHelper,
  getPolicyLimitPIPWLInitialValue,
  michiganPipISubmitValuesHelper,
  getPolicyLimitPIPWLSubmitValue
} from 'common/components/personal-injury-protection/helpers';
import {
  formatValues as formatHomeValues,
  getHighestEducationAppCoApp,
  getOldestAppCoApp
} from 'common/components/home/detail/helper';
import { defaultApplicantRelationshipToInsured } from 'common/components/people/helpers';
import { getStartMinMaxDate } from '../dates';
import { getDateViolations, isAutoBackdateChangeAllowed } from './helpers';
import DetailsForm from './details-form';
import { getSchema } from './validations';

const changeType = {
  EditSelectedSegment: 'edit_change',
  NewSegment: 'new_change'
};

const PolicyDetails = ({ loadingPreview, onSegmentPreview, repEmail, onClose, preview }) => {
  const {
    account: {
      policies: {
        getHomePolicyDetails,
        getAutoPolicyDetails,
        hasActiveOrFutureAutoPolicy,
        list: policies,
        policy: {
          allActiveHoldCards,
          policy,
          segment,
          preview: storePreview,
          isAdvancedConnectedHome,
          geographicState,
          loading,
          scrollTo,
          setScrollTo
        }
      }
    },
    affinityLookups
  } = useStore();

  const session = useContext(AuthContext);
  const { canBackDate, canEditUneditablePropertyDetailsFields, isAgency } = session;

  const { disabled, setDisabled } = useDisabledState();
  const hasHoldCards = !!allActiveHoldCards().length;
  const policyStatus = loading ? null : getPolicyStatus(policy);
  const cancelled = policyStatus === PolicyStatus.InCancellation || policyStatus === PolicyStatus.Cancelled;

  useEffect(() => {
    setDisabled(hasHoldCards || cancelled || !session.canEditPolicies);
  }, [hasHoldCards, setDisabled, disabled, allActiveHoldCards, session, cancelled]);

  // in order to have the accurate umbrella values on Auto, we have to use this function to look at the active home policy details
  const homePolicyDetails = getHomePolicyDetails();

  // in order to calculate the correct MPD on a home policy, we need to use this function to see if renters is attached on the auto.
  // Align to the auto TERM (policy) in force on the edited segment's startDate so umbrella validation
  // compares against the matching auto term (e.g. a Home renewal validates against the auto renewal term,
  // not the current term — see https://github.com/gobranch/branch/issues/32151). All segments within that
  // term are merged, so a mid-term auto endorsement counts even if the home segment started before it.
  // Passing the home term's endDate widens the limit check to every already-created auto term the home
  // term spans (per UW: an existing auto renewal must also carry qualifying limits; a not-yet-created
  // renewal can't be validated and isn't).
  const autoPolicyDetails = getAutoPolicyDetails(segment?.startDate, policy?.endDate);

  const [state, setState] = useState(() =>
    fromJS({
      changed: false,
      changeType: isFuture(new Date(segment.startDate)) ? changeType.EditSelectedSegment : changeType.NewSegment,
      startDate: undefined,
      minDate: undefined,
      maxDate: undefined
    })
  );

  const toast = useToast();

  // Enable scrolling functionality for goToPIPCoverageSection
  useScroll(scrollTo, () => {
    setScrollTo(null);
    onClose();
  });

  // if we're on a home policy, the current segment node in the store is filled with the home policy's segment
  // so we need to grab the autoPolicy from the store and look at it's merged segments or policyDetails for the auto coverage
  // we need autoCoverage even for home policies so we know if the policy is able to include umbrella
  const autoCoverage = policy.policyType === policyTypes.Auto ? segment.autoCoverage : autoPolicyDetails?.autoCoverage;
  const drivers = policy.policyType === policyTypes.Auto ? segment.drivers : autoPolicyDetails?.drivers;

  const segmentDetails = !isFarmBasedPolicyType(policy.policyType)
    ? segment
    : policyDetailsFromSegments(policy, { upToSegmentId: segment.segmentId, keepPremium: true });

  const initialValues = {
    ...segmentDetails,
    // CFM defaults per-vehicle sole ownership to true ("Yes") — the common case — so the Joint Owner
    // select stays disabled/cleared unless the user unchecks it; jointOwner defaults to '' so the
    // select renders a blank input with its label shrunk (matches the offer flow). Flag-gated.
    ...(appConfig?.featureFlags?.supportsVehicleOwnershipDetails
      ? {
          cars: (segmentDetails.cars ?? []).map((car) => ({
            ...car,
            solelyOwnedByApplicant: car?.solelyOwnedByApplicant ?? true,
            jointOwner: car?.jointOwner ?? ''
          }))
        }
      : {}),
    condo: segmentDetails.condo
      ? {
          ...segmentDetails.condo,
          noUnitNumber: !segmentDetails.condo.hasUnitNumber,
          monthsRentedOut: segmentDetails.condo.weeksRentedOut ? Math.floor(segmentDetails.condo.weeksRentedOut / 4) : 0
        }
      : null,
    auto: segmentDetails.auto
      ? {
          ...segmentDetails.auto,
          pipAdditionalResidents: segmentDetails.auto?.pipAdditionalResidents || 0
        }
      : null,
    // Hydrate the flat "Mortgage lender information" form list from per-structure
    // building.mortgageDetails, tagging each with appliesToStructure. Distributed back on submit. (#32526)
    farm: isFarmBasedPolicyType(policy.policyType)
      ? { ...(segmentDetails.farm ?? {}), mortgageDetails: flattenFarmMortgagesForForm(segmentDetails.farmLocations) }
      : segmentDetails.farm,
    // #32405: seed the Details "Policy restrictions" section with the policy's current restriction so
    // a UW can add / change / remove it. This is a form-only field (not part of PolicyDetailsInput);
    // sanitize.js strips it from the segment payload, and policy.jsx forwards the edited value as
    // uwReviewAnswers on confirm.
    restrictions: getFarmPolicyRestriction(policy) || '',
    farmLocations: isFarmBasedPolicyType(policy.policyType)
      ? segmentDetails.farmLocations.filter((location) => !location.isDeleted)
      : null,
    // farmInterestedParties is a policy-level field, read from `policy` (top level), NOT the
    // segment-merged `segmentDetails` — #31942 moved it off the segment. The Details tab still
    // needs it in the form so the shared farm validation passes (e.g. it requires a billToOther
    // party when farmPaymentMethod is BillToOther), but it is NOT sent back: sanitize.js's
    // PolicyDetailsInput no longer includes the field, so changeSegment strips it and the
    // backend preserves the policy-level parties. Editing parties is the Settings tab's job.
    // Hydrate backend format (associatedEntities) into form format so validation/round-trip
    // behaves like policy-settings-container.jsx. Issues #31753, #31945.
    farmInterestedParties: (policy.farmInterestedParties || []).map(hydrateFarmPartyForForm),
    scheduledPersonalProperty: {
      deductible: segmentDetails.scheduledPersonalProperty?.items
        ? segmentDetails.scheduledPersonalProperty?.deductible
        : null,
      items: segmentDetails.scheduledPersonalProperty?.items.length
        ? sortItems(segmentDetails.scheduledPersonalProperty?.items.map((item) => getValues(item)))
        : []
    },
    highestEducation:
      segmentDetails.people || segmentDetails.drivers
        ? getHighestEducationAppCoApp(segmentDetails.people || segmentDetails.drivers)
        : undefined,
    oldestResident:
      segmentDetails.people || segmentDetails.drivers
        ? getOldestAppCoApp(segmentDetails.people || segmentDetails.drivers)
        : undefined,
    multiPolicyDiscount:
      policy.policyType !== policyTypes.Auto
        ? segmentDetails.multiPolicyDiscount
        : getAutoMPD(segmentDetails.multiPolicyDiscount),
    attachedRenters: segmentDetails.attachedRenters || !!segmentDetails.includeRenters,
    drivers: defaultApplicantRelationshipToInsured(drivers),
    autoCoverage:
      policy.policyType === policyTypes.Auto && segmentDetails.autoCoverage
        ? {
            ...segmentDetails.autoCoverage,
            pipAdditionalResidents: segmentDetails.autoCoverage?.pipAdditionalResidents || 0,
            policyLimitPIPWL: getPolicyLimitPIPWLInitialValue(
              geographicState,
              segmentDetails.autoCoverage.policyLimitPIPWL,
              segmentDetails.drivers,
              segmentDetails.drivers?.concat(segmentDetails.nonDrivers)
            ),
            policyLimitPIPACR: michiganPipInitialValuesHelper(
              geographicState,
              'policyLimitPIPACR',
              segmentDetails.autoCoverage.policyLimitPIPACR
            ),
            policyLimitPIPME: michiganPipInitialValuesHelper(
              geographicState,
              'policyLimitPIPME',
              segmentDetails.autoCoverage.policyLimitPIPME,
              segmentDetails.drivers?.concat(segmentDetails.nonDrivers)
            )
          }
        : autoCoverage,
    // Include policy-level additionalParties so the Add Car form can append
    // lienholders via setFieldValue, and the data flows through to handleSegmentChange.
    additionalParties: policy.additionalParties || [],
    underwritingChanges: false,
    isBix: policy.isBix,
    skipImmediateBillOrRefund: policy.skipImmediateBillOrRefund,
    fcraDisclaimer: false
  };

  useEffect(() => {
    if (!state.get('startDate') && policy) {
      const { startDate, minDate, maxDate } = getStartMinMaxDate({
        policy,
        editCurrentSegment: state.get('changeType') === changeType.EditSelectedSegment,
        currentSegment: segment,
        canBackDate,
        policyType: policy.policyType,
        isAgency
      });
      setState(state.set('startDate', startDate).set('minDate', minDate).set('maxDate', maxDate));
    }
  }, [policy, state, segment, canBackDate, isAgency]);

  useEffect(() => {
    // runs when clicking on a different segment in changes history
    // sets default change type based on effective date of segment: future effective defaults to changeType.EditSelectedSegment.
    if (policy && segment?.segmentId && segment.segmentId !== state.get('segmentId')) {
      const { startDate, minDate, maxDate } = getStartMinMaxDate({
        policy,
        editCurrentSegment: isFuture(new Date(segment.startDate)),
        currentSegment: segment,
        canBackDate,
        policyType: policy.policyType,
        isAgency
      });
      setState(
        state
          .set('segmentId', segment.segmentId)
          .set(
            'changeType',
            isFuture(new Date(segment.startDate)) ? changeType.EditSelectedSegment : changeType.NewSegment
          )
          .set('changed', false)
          .set('startDate', startDate)
          .set('minDate', minDate)
          .set('maxDate', maxDate)
      );
    }
  }, [segment, state, setState, policy, canBackDate, isAgency]);

  const handleStartDate = useCallback(
    ({ value: startDate }) => {
      setState(state.set('startDate', startDate));
    },
    [setState, state]
  );

  const handleRadioChange = useCallback(
    (e) => {
      const { startDate, minDate, maxDate } = getStartMinMaxDate({
        policy,
        editCurrentSegment: e.target.value === changeType.EditSelectedSegment,
        currentSegment: segment,
        canBackDate,
        policyType: policy.policyType,
        isAgency
      });
      setState(
        state
          .set('changeType', e.target.value)
          .set('startDate', startDate)
          .set('minDate', minDate)
          .set('maxDate', maxDate)
      );
    },
    [setState, state, canBackDate, policy, segment, isAgency]
  );

  const handleSegmentChange = useCallback(
    (policyDetails) => {
      const residents = policyDetails?.drivers?.concat(policyDetails?.nonDrivers || []) || [];
      const hasDateViolations = getDateViolations({
        isEdit: state.get('changeType') === changeType.EditSelectedSegment,
        policy,
        segment,
        startDate: state.get('startDate'),
        minDate: state.get('minDate'),
        maxDate: state.get('maxDate'),
        toast
      });

      if (!hasDateViolations) {
        // Backdated auto endorsements (start date before today) are limited to add-car / remove-car / add-driver
        // for users without canBackDate. Block the submission if any other change is being made.
        const startDate = state.get('startDate');
        const isBackdated = startDate && isBefore(startOfDay(startDate), startOfDay(new Date()));
        const backdateCheck = !canBackDate
          ? isAutoBackdateChangeAllowed({
              policyType: policy.policyType,
              isBackdated,
              initialValues,
              currentValues: policyDetails
            })
          : { allowed: true };
        if (!backdateCheck.allowed) {
          toast.notify({ type: 'error', message: backdateCheck.reason });
          return;
        }

        // If we're turning on inventory then set the discountInventoryScoreAddedDate
        if (!segment.global.discountInventoryScore && policyDetails.global.discountInventoryScore) {
          policyDetails.global.discountInventoryScoreAddedDate = dateFnsFormat(new Date(), 'yyyy-MM-dd');
        }

        if (policy.policyType === policyTypes.Auto && geographicState === 'MI') {
          const totalResidents = policyDetails.drivers.length + (policyDetails.nonDrivers || []).length;
          policyDetails.auto.pipAllResidents = totalResidents;
          if (totalResidents === 1) {
            policyDetails.auto.pipEveryoneOnSamePlan = true;
          }
        }

        // if a user with this permission group updates sqft on a home policy to a lesser value than the original sqft, update original sqft
        // to base future validations on this value
        if (
          policy.policyType === policyTypes.Home &&
          canEditUneditablePropertyDetailsFields &&
          policyDetails?.home?.sqFt < segment?.home?.originalSqFt
        ) {
          policyDetails.home.originalSqFt = policyDetails.home.sqFt;
        }

        onSegmentPreview(
          {
            ...policyDetails,
            drivers: policyDetails.drivers?.map((driver) => types.PersonDetailsInput(driver)),
            people: policyDetails.people?.map((person) => types.PersonDetailsInput(person)),
            global: policyDetails?.global
              ? {
                  ...policyDetails.global,
                  manualInventorySubmission:
                    policyDetails.global.discountInventoryScore && policyDetails.global.manualInventorySubmission
                }
              : null,
            umbrellaCoverage: formatUmbrellaValues(policyDetails.umbrellaCoverage, policyDetails.includeUmbrella),
            // Don't send farmInterestedParties from the Details (segment) tab — it's a policy-level
            // field, edited via the Settings tab, and #31942 removed it from PolicyDetailsInput.
            // initialValues loads it (in form format) only so validation passes; override to
            // undefined here so the segment payload never carries it. Issues #31753, #31945.
            farmInterestedParties: undefined,
            scheduledPersonalProperty: formatSPP(policyDetails.scheduledPersonalProperty),
            home: formatHomeValues(policyDetails.home),
            condo: formatCondoValues(policyDetails.condo),
            startDate: awsDateFormatter(state.get('startDate')),
            ...mapDiscountToInputs({ policyDetails }),

            autoCoverage:
              policyDetails.autoCoverage && policy.policyType === policyTypes.Auto
                ? geographicState === 'MI'
                  ? {
                      ...policyDetails.autoCoverage,
                      policyLimitPIPWL: getPolicyLimitPIPWLSubmitValue(
                        geographicState,
                        policyDetails?.autoCoverage.policyLimitPIPWL,
                        policyDetails?.drivers,
                        policyDetails?.drivers?.concat(policyDetails?.nonDrivers)
                      ),
                      policyLimitPIPACR: michiganPipISubmitValuesHelper(
                        geographicState,
                        'policyLimitPIPACR',
                        policyDetails?.autoCoverage.policyLimitPIPACR
                      ),
                      policyLimitPIPME: michiganPipISubmitValuesHelper(
                        geographicState,
                        'policyLimitPIPME',
                        policyDetails?.autoCoverage.policyLimitPIPME,
                        policyDetails?.drivers?.concat(policyDetails?.nonDrivers)
                      )
                    }
                  : { ...policyDetails.autoCoverage }
                : null,
            auto:
              policyDetails.auto && policy.policyType === policyTypes.Auto
                ? geographicState === 'MI'
                  ? {
                      ...policyDetails.auto,
                      pipAllResidents: residents?.length,
                      pipAdditionalResidents: residents?.filter((resident) => resident.pipOptOut).length,
                      pipExcludedResidents: residents?.filter((resident) => resident.pipOptOut).length,
                      pipResidentsWithQHC: residents?.filter((resident) => resident.pipHasQHC).length
                    }
                  : { ...policyDetails.auto }
                : null,
            homeCoverage:
              policyDetails.homeCoverage && policy.policyType === policyTypes.Home
                ? {
                    ...policyDetails.homeCoverage,
                    coverageRSExtended: coverageRSExtendedForcedStates[geographicState]
                      ? true
                      : policyDetails.homeCoverage.coverageRSExtended
                  }
                : null,
            // Distribute the flat "Mortgage lender information" list back onto each structure's
            // building.mortgageDetails (canonical per-structure storage), then clear the legacy flat
            // farm.mortgageDetails so it isn't dual-written to the deprecated field. (#32526)
            ...(isFarmBasedPolicyType(policy.policyType)
              ? {
                  farmLocations: applyFarmMortgagesToLocations(
                    policyDetails.farmLocations,
                    policyDetails.farm?.mortgageDetails
                  ),
                  farm: { ...(policyDetails.farm ?? {}), mortgageDetails: [] }
                }
              : {})
          },
          state.get('changeType'),
          initialValues
        );
      }
    },
    [onSegmentPreview, state, homePolicyDetails, segment, policy, toast, canBackDate, initialValues]
  );

  if (!segment || !policy) {
    return <Loading />;
  }

  const validate = async (values) => {
    try {
      const schema = await getSchema(policy.policyType, {
        policies,
        policy,
        isAdvancedConnectedHome,
        savedSegment: segment,
        session,
        affinityLookups,
        autoPolicyDetails
      });
      await schema.validate(values, {
        abortEarly: false,
        context: {
          ...values,
          state: geographicState,
          initialValues,
          canAddCarsManually: session.canAddCarsManually,
          // #31836: lets the FO6 Coverage E ACV validation relax its 80% floor to a
          // $1,000 minimum for underwriters only (also on existing-policy endorsements).
          isUnderwriter: isUnderwriter(session),
          // #33509: a UW on a migrated-and-renewed farm term may bypass migration-relaxable required
          // fields (e.g. roof shape) that the converter couldn't fill, so they don't block edits.
          relaxFarmMigrationRequirements: shouldRelaxFarmUWRequirements({
            canApproveUWReview: session.canApproveUWReview,
            // Read the flag across the lineage: the FE surfaces the imported (flag-less) in-force term
            // as `policy`, but the UW is endorsing its renewal, which carries the flag. #33509
            wasRenewedFromImportedTerm: wasRenewedFromImportedLineage(policy, policies)
          }),
          affinityLookups,
          renewalVariables: policy.renewalVariables,
          hasActiveOrFutureAutoPolicy
        }
      });
    } catch (errors) {
      if (errors?.name?.includes('TypeError')) {
        console.log({ errors });
        Sentry.captureException(errors, {
          extra: {
            formValues: values,
            validationFailedToRun: true,
            inPolicyDetails: true,
            id: policy.id
          }
        });
        toast.notify({
          type: 'error',
          message: 'Error - Cannot validate this policy modification. Please escalate to development.'
        });
      }

      return yupToFormErrors(errors);
    }
  };

  return (
    <Formik
      key={`${segment.segmentId}-${policy.version}${storePreview?.id ? `-${storePreview.id}` : ''}`}
      initialValues={initialValues}
      validateOnBlur
      // Don't run the heavy async whole-policy getSchema()+validate on every keystroke — it made
      // typing (e.g. the mortgage loan number) laggy. Validation still runs on blur and on the
      // explicit validateForm() the footer fires before submit (details-form.jsx). (#31985)
      validateOnChange={false}
      onSubmit={handleSegmentChange}
      validate={validate}
    >
      <DetailsForm
        loadingPreview={loadingPreview}
        handleRadioChange={handleRadioChange}
        handleStartDate={handleStartDate}
        state={state}
        repEmail={repEmail}
        preview={preview}
      />
    </Formik>
  );
};

PolicyDetails.propTypes = {
  loadingPreview: PropTypes.bool.isRequired,
  onSegmentPreview: PropTypes.func.isRequired,
  repEmail: PropTypes.string.isRequired,
  onClose: PropTypes.func.isRequired,
  preview: PropTypes.object
};

export default observer(PolicyDetails);
